"use client";

import { getSlots } from "@faire/slate/lib/getSlots";
import { getTypedChildren } from "@faire/slate/lib/getTypedChildren";
import { componentCls } from "@faire/slate/lib/tracking";
import {
  MENU_DEFAULT_ALIGNMENT,
  MENU_DEFAULT_Z_INDEX,
} from "@faire/slate/private/menu/consts";
import { MenuBase } from "@faire/slate/private/menu/MenuBase";
import { MenuItemBase } from "@faire/slate/private/menu/MenuItemBase";
import { MenuPortal } from "@faire/slate/private/menu/MenuPortal";
import { MenuRoot } from "@faire/slate/private/menu/MenuRoot";
import type { MenuAlignment } from "@faire/slate/private/menu/types";
import { IMenuWebProps } from "@faire/slate/private/menu/types";
import React from "react";

import { MenuItem } from "./MenuItem";
import { MenuTrigger } from "./MenuTrigger";

type IProps = {
  /**
   * The alignment of the menu in reference to the trigger element.
   *
   * @default end
   */
  alignment?: MenuAlignment;

  /**
   * **web**
   * The z-index of the menu.
   *
   * @default 5
   */
  zIndex?: number;
} & IMenuWebProps;

/**
 * A menu is a list of links or actions that sits on top of another surface.
 *
 * Menu accepts only MenuTrigger and MenuItem as children.
 *
 * > **Nested menus (submenus) are not supported yet.** There is no accessibility problem with them; they have just not been implemented. Drag-reorder is not supported (not accessible). See the [migration guide](migration-guide.mdx).
 *
 * ```tsx
 * <Menu>
 *   <MenuTrigger>
 *     <Button>Open Menu</Button>
 *   </MenuTrigger>
 *   <MenuItem>Option 1</MenuItem>
 *   <MenuItem>Option 2</MenuItem>
 * </Menu>
 * ```
 *
 * {@link https://www.notion.so/faire/Menu-2bc2efb5c25a80c4835bcdff4ff94828 | Slate/Menu}
 */
export const Menu: React.FC<React.PropsWithChildren<IProps>> = ({
  alignment = MENU_DEFAULT_ALIGNMENT,
  children,
  zIndex = MENU_DEFAULT_Z_INDEX,
  ...props
}) => {
  const [{ trigger }, otherChildren] = getSlots(children, {
    trigger: MenuTrigger,
  });
  const [{ items, baseItems }, rest] = getTypedChildren(otherChildren, {
    items: MenuItem,
    baseItems: MenuItemBase,
  });

  if (rest.length > 0) {
    // oxlint-disable-next-line no-console
    console.error("Menu accepts only MenuTrigger, MenuItem, and MenuItemBase");
  }

  const hasItemWithIcon = items.some((item) => item.props.Icon != null);

  return (
    <MenuRoot alignment={alignment}>
      {trigger}
      <MenuPortal>
        <MenuBase
          zIndex={zIndex}
          {...props}
          hasIcons={hasItemWithIcon}
          className={componentCls()}
          data-component-name="Slate/Menu"
        >
          {items}
          {baseItems}
        </MenuBase>
      </MenuPortal>
    </MenuRoot>
  );
};
